import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal, viewChild } from '@angular/core';
import {
  IonCol,
  IonGrid,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonRadio,
  IonRadioGroup,
  IonRow,
} from '@ionic/angular/standalone';
import { DecaCompanyRole, IDecaDocument, IDecaDocumentCompany } from '@okcargo-apps/shared-domain';
import { ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { AccountService } from '../../account';
import { mapDocumentToRole, mapDocumentToTransfer } from '../deca-document.mappers';
import { DecaEditHostComponent } from '../deca-edit-host/deca-edit-host.component';
import { DECA_ROLE, isDecaRole, otherDecaRole } from '../deca-role.utils';
import { DecaStoreService } from '../deca-store.service';

export { DecaCompanyRole } from '@okcargo-apps/shared-domain';

@Component({
  selector: 'lib-deca-edit-selector',
  templateUrl: './deca-edit-selector.component.html',
  styleUrls: ['./deca-edit-selector.component.scss'],
  imports: [
    IonIcon,
    IonItem,
    IonLabel,
    IonGrid,
    IonRow,
    IonCol,
    IonList,
    IonRadioGroup,
    IonRadio,
    DecaEditHostComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DecaEditSelectorComponent {
  public readonly postSaveRoute = input.required<string>();

  private readonly accountService = inject(AccountService);
  private readonly decaStoreService = inject(DecaStoreService);

  private readonly editComponent = viewChild(DecaEditHostComponent);

  protected readonly account = this.accountService.account;

  protected readonly canCreateAsCarrier = computed(() =>
    this.accountService.hasCapability(ECapabilities.CREATE_DECA_AS_CARRIER),
  );
  protected readonly canCreateAsShipper = computed(() =>
    this.accountService.hasCapability(ECapabilities.CREATE_DECA_AS_SHIPPER),
  );

  protected readonly selectedRole = signal<DecaCompanyRole | null>(null);

  protected readonly DECA_ROLE = DECA_ROLE;

  public readonly currentRole = this.selectedRole.asReadonly();

  public readonly hasSelectedRole = computed(() => !!this.selectedRole());
  public readonly hasAvailableCredits = computed(() => this.decaStoreService.availableCredits() > 0);

  protected readonly soleRole = computed<DecaCompanyRole | null>(() => {
    if (this.canCreateAsCarrier() === this.canCreateAsShipper()) return null;
    return this.canCreateAsCarrier() ? DECA_ROLE.CARRIER : DECA_ROLE.SHIPPER;
  });

  protected readonly document = signal<IDecaDocument | null>(null);

  constructor() {
    effect(() => {
      const role = this.soleRole();
      if (role && !this.selectedRole() && !this.document()) {
        this.setRole(role);
      }
    });
  }

  protected onRoleChange(event: Event): void {
    const value = (event as CustomEvent).detail?.value;
    if (isDecaRole(value)) {
      this.setRole(value);
    }
  }

  public checkDraftInStore(): void {
    this.editComponent()?.checkDraftInStore();
  }

  public cleanForm(): void {
    this.editComponent()?.cleanForm();
  }

  public onSubmit(): Promise<void> {
    return this.editComponent()?.onSubmit() ?? Promise.resolve();
  }

  public resetRoleSelection(): void {
    this.selectedRole.set(null);
    this.document.set(null);
  }

  public initWithDocument(copy: IDecaDocument): void {
    const companyId = this.accountService.account().company.id;

    const role: DecaCompanyRole | null =
      copy.shipper.id === companyId && this.canCreateAsShipper()
        ? DECA_ROLE.SHIPPER
        : copy.carrier.id === companyId && this.canCreateAsCarrier()
          ? DECA_ROLE.CARRIER
          : null;

    if (!role) return;

    this.selectedRole.set(role);
    this.document.set(copy);
  }

  public initWithTransfer(previousDocument: IDecaDocument): void {
    if (!this.canCreateAsShipper()) return;

    this.selectedRole.set(DECA_ROLE.SHIPPER);
    this.document.set(mapDocumentToTransfer(previousDocument, this.getCurrentUserCompany()));
  }

  private setRole(role: DecaCompanyRole): void {
    const previousRole = this.selectedRole();
    if (role === previousRole) return;

    const previousCounterpartyRole = previousRole ? otherDecaRole(previousRole) : DECA_ROLE.CARRIER;
    const previousCompanyState = previousRole
      ? this.editComponent()?.getCompanyFormState(previousCounterpartyRole)
      : undefined;

    const previousDocument = this.editComponent()?.getCurrentDocumentDraft() ?? this.document();
    this.selectedRole.set(role);
    this.document.set(mapDocumentToRole(role, previousRole, previousDocument, this.getCurrentUserCompany()));

    if (previousRole) {
      const editableRole = otherDecaRole(role);
      this.editComponent()?.setCompanyMode(editableRole, previousCompanyState?.mode ?? 'summary');
    }
  }

  private getCurrentUserCompany(): IDecaDocumentCompany {
    const account = this.account();
    const company = account.company;

    return {
      id: company.id,
      companyName: company.name,
      taxId: company.taxNumber,
      street: company.streetAddress,
      city: company.city,
      postalCode: company.postalCode,
      stateCode: company.stateCode,
      countryCode: company.countryCode,
      contactId: account.contactId,
      contactFirstName: account.firstName,
      contactLastName: account.lastName,
      contactEmail: account.email,
      contactPhone: account.phone ?? '',
    };
  }
}
